import React, { createContext, useState, useCallback, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Modal } from '@encarpkg/design';
import useLayer from 'app/hook/common/useLayer';
import useAuth from 'app/hook/common/useAuth';
import useGoLogin from 'app/hook/common/useGoLogin';
import { loggingException } from 'app/libs/sentry';
import qs from 'query-string';
import { useUniversalCloser } from 'app/hook/common/useUniversalCloser';
import { goHome } from 'app/libs/location';

const ERROR_TYPE = {
	ALREADY_APPLIED_CAR: 'ALREADY_APPLIED_CAR',
	ALREADY_APPLIED_USER: 'ALREADY_APPLIED_USER',
	CANNOT_APPLY: 'CANNOT_APPLY',
};
const IS_NON_MEMBER = 'isNonMember';

export const IsAvailableToApplyContext = createContext();

const IsAvailableToApplyProvider = ({ children = null }) => {
	const [errorType, setErrorType] = useState('');
	const [isNonMember, setIsNonMember] = useState(null);
	const location = useLocation();
	const params = useParams();
	const goLogin = useGoLogin();
	const search = qs.parse(location.search);
	const carId = params.carId || search.carId;
	// 알림톡 진입 시에만 전달되는 출장검수 예약ID (신청자 본인 여부 판단용)
	const salesAgentReservationId = params.salesAgentReservationId || search.salesAgentReservationId;
	const { view, handleLayerOpen, handleLayerClose } = useLayer();
	const { userInfo, sessionCheckInfo } = useAuth();
	const isBirthNone = !userInfo?.birth;
	const { handleUniversalClose } = useUniversalCloser({
		browser: {
			onNotClosed: goHome,
		},
	});

	const handleLayerSubmit = useCallback(() => {
		handleUniversalClose();
	}, [handleUniversalClose]);

	const handleLayerCloseExtend = useCallback(() => {
		handleLayerClose();
		setErrorType('');
		handleUniversalClose();
	}, [handleLayerClose, handleUniversalClose]);

	const isAvailableToApply = useCallback(
		async (checkApi) => {
			if (!checkApi) return false;

			try {
				await sessionCheckInfo();
			} catch (error) {
				alert('일정 시간 동안 이용하시지 않아서 자동 로그아웃 되었습니다. \n엔카믿고 신청을 다시 진행해 주세요.');

				goLogin({
					returnUrl: encodeURIComponent(`${location.pathname}${location.search}`),
				});

				return false;
			}

			const userId = userInfo?.id;
			const name = userInfo?.name;
			const phone = userInfo?.phone;
			const userGrade = userInfo?.grade;

			if (userGrade !== 'INDIVIDUAL') {
				alert('개인 회원만 이용 가능한 서비스 입니다.');
				handleLayerSubmit();
				return false;
			}

			try {
				const { apply, userApply, carApply, condition, homeServiceCarActive, onSale } = await checkApi({
					carId,
					userId,
					name,
					phone,
					salesAgentReservationId,
				}).then((res) => res.data);

				if (apply) {
					return true;
				}

				let status;

				if (!userApply) {
					status = ERROR_TYPE.ALREADY_APPLIED_USER;
				} else if (!carApply) {
					status = ERROR_TYPE.ALREADY_APPLIED_CAR;
				} else if (!(condition && homeServiceCarActive && onSale)) {
					status = ERROR_TYPE.CANNOT_APPLY;
				}

				setErrorType(status);
				handleLayerOpen();
			} catch (error) {
				loggingException(error);
			}

			return false;
		},
		[carId, salesAgentReservationId, goLogin, handleLayerOpen, handleLayerSubmit, location.pathname, location.search, sessionCheckInfo, userInfo],
	);

	const loingAfterPassIntro = () => {
		setIsNonMember(false);
		window.sessionStorage.setItem(IS_NON_MEMBER, false);
	};

	useEffect(() => {
		// 신청 도중 로그인 여부가 달라지더라도 처음 설정한 값으로 유지
		const isNonMemberInSessionStorage = window.sessionStorage.getItem(IS_NON_MEMBER);
		if (isNonMemberInSessionStorage) {
			setIsNonMember(JSON.parse(isNonMemberInSessionStorage));

			return;
		}

		if (isNonMember === null) {
			(async () => {
				try {
					await sessionCheckInfo();
					setIsNonMember(false);
					window.sessionStorage.setItem(IS_NON_MEMBER, false);
				} catch (error) {
					setIsNonMember(true);
					window.sessionStorage.setItem(IS_NON_MEMBER, true);
				}
			})();
		}
	}, [isNonMember, sessionCheckInfo]);

	let layerTitle = '';

	switch (errorType) {
		case ERROR_TYPE.ALREADY_APPLIED_CAR:
			layerTitle = (
				<>
					앗! 방금 다른분께서
					<br />
					엔카믿고를 신청하셨습니다.
				</>
			);
			break;
		case ERROR_TYPE.ALREADY_APPLIED_USER:
			layerTitle = (
				<>
					엔카믿고 신청은
					<br />한 번에 한 건씩만 할 수 있어요
				</>
			);
			break;
		case ERROR_TYPE.CANNOT_APPLY:
			layerTitle = (
				<>
					차량 상태가 변경되어
					<br />
					엔카믿고 신청을 할 수 없어요
				</>
			);
			break;
		default:
	}

	return (
		<IsAvailableToApplyContext.Provider
			value={{
				isNonMember,
				isAvailableToApply,
				isBirthNone,
				loingAfterPassIntro,
			}}
		>
			{children}
			{view && (
				<Modal
					isOpen
					dimClosable={false}
					onClickOk={handleLayerSubmit}
					onClose={handleLayerCloseExtend}
					title={layerTitle}
					okText="확인"
					subtitle={
						<>
							{errorType === ERROR_TYPE.ALREADY_APPLIED_CAR && (
								<>
									아직 계약이 완료된것은 아닙니다.
									<br />
									신청자와
									<span style={{ color: '#ff2626' }}> 전화 상담 후 계약</span>
									이 완료되오니
									<br />
									해당 매물을 예의주시하세요.
								</>
							)}

							{errorType === ERROR_TYPE.ALREADY_APPLIED_USER && (
								<>
									이 차량 구매를 원하시면
									<br />
									다른 차량의 엔카믿고 신청을 취소해 주세요.
								</>
							)}

							{errorType === ERROR_TYPE.CANNOT_APPLY && (
								<>
									차량이 판매됐거나
									<br />
									상태를 재점검중일 수 있어요.
									<br />
									다른 차량에 엔카믿고 신청 해주세요.
								</>
							)}
						</>
					}
				/>
			)}
		</IsAvailableToApplyContext.Provider>
	);
};

export default IsAvailableToApplyProvider;
